import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { authEndpoints } from "@/endpoints";
import useAuth from "@/providers/hooks/useAuthProvider";

// User data type definition
export interface UserData {
    id: string;
    name: string;
    email: string;
    extraPermissions?: string[];
    blockedPermissions?: string[];
    permissionCodes?: string[]; // Calculated all permission codes
    permissionGroups: {
        id: string;
        name: string;
        permissions: string[];
        color: string;
    }[];
    provider: string;
    disabled: boolean;
    createdAt: string;
}

// Global cache - different components can access it
let userDataCache: UserData | null = null;
let listeners: (() => void)[] = [];

// Global refresh status - to prevent repeated calls
let isCurrentlyRefreshing = false;
let lastRefreshTime = 0;
// Debounce time (300ms) - prevents short intervals
const DEBOUNCE_TIME = 300;

// Add/remove listener functions
export const addUserDataListener = (listener: () => void) => {
    listeners.push(listener);
    return () => {
        listeners = listeners.filter((l) => l !== listener);
    };
};

// Update cache and notify listeners
export const updateUserDataCache = (data: UserData | null) => {
    userDataCache = data;
    listeners.forEach((listener) => listener());
};

// Get data from cache
export const getUserDataCache = (): UserData | null => {
    return userDataCache;
};

/**
 * Handle user logout and redirect
 * This is a global function that can be used from anywhere
 */
export const handleAuthError = () => {
    // Clear user session from localStorage
    localStorage.removeItem("session");
    localStorage.removeItem("user");

    // Clear user data cache
    updateUserDataCache(null);

    // TODO: Since we can't use hooks in a regular function, we need to handle this in the component
    toast.error("Your session has expired. Please log in again.");

    // Force reload the page to clear all states
    window.location.href = "/dashboard/home";
};

/**
 * Automatically synchronizes and refreshes user permissions
 *
 * @param refreshInterval Refresh interval (ms, default: 60000ms - 1 minute)
 * @param skipInitialFetch Skip fetch on initial load (used when multiple components are used in the sidebar)
 */
export const useUserPermissionsSync = (refreshInterval = 60000, skipInitialFetch = false) => {
    const { auth, setAuth } = useAuth();
    const [loading, setLoading] = useState(false);

    const refreshUserPermissions = useCallback(async () => {
        if (!auth?.accessToken) return false;

        // Prevent multiple calls at the same time
        const now = Date.now();

        // If a refresh is currently being made, skip the request
        if (isCurrentlyRefreshing) {
            return false;
        }

        // Debounce check - prevents short intervals
        if (now - lastRefreshTime < DEBOUNCE_TIME) {
            return false;
        }

        // Lock the refresh
        isCurrentlyRefreshing = true;
        setLoading(true);

        try {
            const response = await axios.get(authEndpoints.me, {
                headers: { Authorization: `Bearer ${auth.accessToken}` },
            });

            lastRefreshTime = Date.now();

            if (response.data) {
                // Get all permission codes (from permissionGroups) into a single array
                const allPermissions: string[] = [];

                // Loop through permission groups and get all codes
                if (response.data.permissionGroups && response.data.permissionGroups.length > 0) {
                    response.data.permissionGroups.forEach((group) => {
                        if (group.permissions && Array.isArray(group.permissions)) {
                            group.permissions.forEach((perm) => {
                                if (!allPermissions.includes(perm)) {
                                    allPermissions.push(perm);
                                }
                            });
                        }
                    });
                }

                // Add extra permissions
                if (response.data.extraPermissions && Array.isArray(response.data.extraPermissions)) {
                    response.data.extraPermissions.forEach((perm) => {
                        if (!allPermissions.includes(perm)) {
                            allPermissions.push(perm);
                        }
                    });
                }

                // Remove blocked permissions
                if (response.data.blockedPermissions && Array.isArray(response.data.blockedPermissions)) {
                    response.data.blockedPermissions.forEach((perm) => {
                        const index = allPermissions.indexOf(perm);
                        if (index !== -1) {
                            allPermissions.splice(index, 1);
                        }
                    });
                }

                // Enrich user data with permission codes
                const userData: UserData = {
                    ...response.data,
                    permissionCodes: allPermissions,
                };

                // Update cache
                updateUserDataCache(userData);

                // If new token is obtained, update auth state
                if (response.data.accessToken && response.data.refreshToken) {
                    setAuth({
                        accessToken: response.data.accessToken,
                        refreshToken: response.data.refreshToken,
                    });
                }

                // Unlock the refresh
                isCurrentlyRefreshing = false;
                setLoading(false);
                return true;
            }
        } catch (error) {
            console.error("❌ User permissions could not be updated:", error);

            // Check if the error is a 401 or 403 unauthorized error
            if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
                // Clear auth state before calling handleAuthError
                setAuth(null);
                // Handle unauthorized error - logout and redirect
                handleAuthError();
            }

            // Unlock the refresh even if there is an error
            isCurrentlyRefreshing = false;
            setLoading(false);
            return false;
        }

        // Unlock the refresh if we get here without returning true
        isCurrentlyRefreshing = false;
        setLoading(false);
        return false;
    }, [auth, setAuth]);

    useEffect(() => {
        // Run on initial load (if skipInitialFetch=false)
        if (!skipInitialFetch) {
            refreshUserPermissions();
        }

        // Set periodic check
        const interval = setInterval(refreshUserPermissions, refreshInterval);
        return () => clearInterval(interval);
    }, [refreshUserPermissions, refreshInterval, skipInitialFetch]);

    return { refreshUserPermissions, loading, userDataCache: getUserDataCache() };
};

export default useUserPermissionsSync;
